import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";

import {ClubAPI} from "@/api/club";
import {MatchAPI} from "@/api/match";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	ERROR_OPERATION,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {checkRequiredFields} from "@/utils/functions";
import {useCategories, useClubs} from "@/utils/hooks";

const HandleScoutingMatchPage = (props) => {
	const {edit, handleNotification} = props;

	const basePath = "/scouting/planning";

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification,
	});
	const {clubs} = useClubs({setLoading: false, handleNotification});

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});

	const getMatch = async () => {
		setLoading(true);
		try {
			const response = await MatchAPI.getById(id, {seasonId});
			response.startDate = response.startDate
				? new Date(response.startDate)
				: null;
			response.category = {
				value: response.playersCategoryId,
				label: response.playersCategory,
			};
			response.homeClub = {
				value: response.homeClubId,
				label: response.homeClub,
			};
			response.awayClub = {
				value: response.awayClubId,
				label: response.awayClub,
			};
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getMatch();
		}
	}, [edit]);

	const getRequiredFields = () => {
		return edit
			? ["startDate", "homeClub", "awayClub"]
			: ["startDate", "category", "homeClub", "awayClub"];
	};

	const saveMatch = async () => {
		try {
			const errors = checkRequiredFields(getRequiredFields(), fields);
			setErrors(errors);

			if (!errors.length) {
				setLoading(true);

				let body = Object.assign(
					{},
					{
						...fields,
						playersCategoryId: fields?.category.value,
						homeClubId: fields?.homeClub.value,
						awayClubId: fields?.awayClub.value,
						observedMatch: true,
					},
				);

				delete body.category;
				delete body.homeClub;
				delete body.awayClub;

				const response = edit
					? await MatchAPI.update(id, body)
					: await MatchAPI.create(body);

				setTimeout(() => {
					navigate(`${basePath}/${response?.id || id}`);
					setLoading(false);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			}
		} catch (error) {
			setLoading(false);
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const createClub = async (name, fieldKey) => {
		try {
			const response = await ClubAPI.create({name});
			setFields({
				...fields,
				[fieldKey]: {value: response.id, label: response.name},
			});
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveMatch();
	};

	const goBack = () => {
		if (edit) {
			navigate(basePath + "/" + id);
		} else {
			navigate(-1);
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [{label: "Partite", path: basePath}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields
					? (fields.homeClub?.label || "") +
						" - " +
						(fields.awayClub?.label || "")
					: "",
				path: basePath + "/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-12 col-md-6">
										<VLFDatePicker
											type="datetime"
											label="Data inizio"
											value={fields.startDate}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, startDate: value});
											}}
											required
											helperText={
												errors.indexOf("startDate") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("startDate") !== -1}
											size="small"
										/>
									</div>
									<div className="col-12 col-md-6">
										<VLFTextField
											value={fields.fieldName}
											onChange={(e) => {
												setFields({...fields, fieldName: e.target.value});
											}}
											label="Luogo"
											size="small"
										/>
									</div>
									<div className="col-12 col-md-6">
										<VLFTextField
											value={fields.scoutNames}
											onChange={(e) => {
												setFields({...fields, scoutNames: e.target.value});
											}}
											label="Osservatori"
											size="small"
										/>
									</div>
									<div className="col-12 col-md-6">
										<VLFSelectNew
											options={categories.map((c) => {
												return {
													value: c.id,
													label: c.name,
												};
											})}
											value={edit ? fields.playersCategory : fields.category}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, category: newValue});
											}}
											sortOrder="descend"
											label="Categoria"
											required
											editable={!edit}
											helperText={
												errors.indexOf("category") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("category") !== -1}
										/>
									</div>
									<div className="col-12 col-md-6">
										<VLFSelectNew
											options={clubs.map((c) => {
												return {
													value: c.id,
													label: c.name,
												};
											})}
											value={fields.homeClub}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, homeClub: newValue});
											}}
											label="Squadra casa"
											onCreateOption={(value) => createClub(value, "homeClub")}
											required
											helperText={
												errors.indexOf("homeClub") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("homeClub") !== -1}
										/>
									</div>
									<div className="col-12 col-md-6">
										<VLFSelectNew
											options={clubs.map((c) => {
												return {
													value: c.id,
													label: c.name,
												};
											})}
											value={fields.awayClub}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, awayClub: newValue});
											}}
											label="Squadra ospite"
											onCreateOption={(value) => createClub(value, "awayClub")}
											required
											helperText={
												errors.indexOf("awayClub") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("awayClub") !== -1}
										/>
									</div>
									<div className="col-12 col-md-6">
										<VLFTextField
											value={fields.homeClubGoals}
											onChange={(e) => {
												setFields({...fields, homeClubGoals: e.target.value});
											}}
											label="Goal casa"
											size="small"
										/>
									</div>
									<div className="col-12 col-md-6">
										<VLFTextField
											value={fields.awayClubGoals}
											onChange={(e) => {
												setFields({...fields, awayClubGoals: e.target.value});
											}}
											label="Goal ospite"
											size="small"
										/>
									</div>
									<div className="col-12 col-md-6">
										<VLFTextField
											value={fields["description"] || ""}
											onChange={(e) => {
												setFields({...fields, description: e.target.value});
											}}
											label="Descrizione"
											size="small"
											multiline
											minRows={3}
											maxRows={3}
										/>
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleScoutingMatchPage;
